import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { LoginType } from "@/contexts/AuthContext";

export default function LoginPage() {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [loginType, setLoginType] = useState<LoginType>("dealer");
  const [dealerCode, setDealerCode] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Support ticket modal state
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportForm, setSupportForm] = useState({ dealerCode: "", subject: "", message: "", phone: "" });
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportResult, setSupportResult] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  if (isAuthenticated && user) {
    const isAvullar = user.dealerName?.toLowerCase().includes("avullar") || false;
    if (user.loginType === "dealer_admin") {
      return <Navigate to={isAvullar ? "/restaurant-pos" : "/dealer-admin/currencies"} replace />;
    }
    if (isAvullar) {
      return <Navigate to="/restaurant-pos" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(loginType, dealerCode, identifier, password);
    if (result.success) {
      const isAvullar = result.dealerName?.toLowerCase().includes("avullar") || false;
      if (isAvullar) {
        navigate("/restaurant-pos");
      } else if (loginType === "dealer_admin") {
        navigate("/dealer-admin/currencies");
      } else {
        navigate("/dashboard");
      }
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const isDealerAdmin = loginType === "dealer_admin";

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formEl = e.currentTarget as HTMLFormElement;
    const honeypotField = formEl.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== "") {
      setSupportResult({ type: "success", msg: "Destek talebiniz alındı, en kısa sürede dönüş yapılacaktır." });
      setShowSupportModal(false);
      setSupportForm({ dealerCode: "", subject: "", message: "", phone: "" });
      setSupportResult(null);
      return;
    }
    if (!supportForm.subject.trim() || !supportForm.message.trim()) return;

    setSupportSubmitting(true);
    setSupportResult(null);

    try {
      const formData = new FormData(formEl);
      const res = await fetch("https://readdy.ai/api/form/d9h12s0h9dsfbuoi5bjg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as any).toString(),
      });
      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === "OK") {
        setSupportResult({ type: "success", msg: "Destek talebiniz alındı, en kısa sürede dönüş yapılacaktır." });
        setSupportForm({ dealerCode: "", subject: "", message: "", phone: "" });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (String(serverMsg).includes("spam") || String(serverMsg).includes("form data is spam")) {
          setSupportResult({ type: "error", msg: "Destek talebiniz alınamadı. Lütfen daha sonra tekrar deneyin." });
        } else {
          setSupportResult({ type: "error", msg: String(serverMsg).slice(0, 200) || "Bir hata oluştu, lütfen tekrar deneyin." });
        }
      }
    } catch {
      setSupportResult({ type: "error", msg: "Bağlantı hatası. Lütfen internet bağlantınızı kontrol edip tekrar deneyin." });
    } finally {
      setSupportSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
      <div className="w-full max-w-md">
        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4">
              <img
                src="https://public.readdy.ai/ai/img_res/0bc3c21d-f175-4039-a061-cbb43a619b2e.png"
                alt="ART POS Logo"
                className="w-32 h-32 mx-auto object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 font-heading">ART POS</h1>
            <p className="text-foreground-600 mt-1 text-sm">Bayi Giriş Paneli</p>
          </div>

          {/* Login Type Tabs */}
          <div className="flex bg-background-100 rounded-full p-1 mb-6">
            <button
              onClick={() => { setLoginType("dealer"); setError(""); }}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${loginType === "dealer" ? "bg-background-50 text-foreground-950" : "text-foreground-500 hover:text-foreground-700"}`}
            >
              <i className="ri-building-line mr-1.5"></i>Bayi Girişi
            </button>
            <button
              onClick={() => { setLoginType("dealer_admin"); setError(""); }}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${loginType === "dealer_admin" ? "bg-background-50 text-foreground-950" : "text-foreground-500 hover:text-foreground-700"}`}
            >
              <i className="ri-shield-user-line mr-1.5"></i>Bayi Admin
            </button>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Bayi Kodu
              </label>
              <div className="relative">
                <i className="ri-key-2-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
                <input
                  type="text"
                  value={dealerCode}
                  onChange={(e) => setDealerCode(e.target.value)}
                  placeholder="Bayi kodunuzu girin"
                  className="w-full pl-10 pr-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  autoComplete="off"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Bayi Adı veya Kullanıcı Adı
              </label>
              <div className="relative">
                <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="Bayi adınızı veya kullanıcı adınızı girin"
                  className="w-full pl-10 pr-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  autoComplete="off"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                {isDealerAdmin ? "Bayi Admin Şifresi" : "Şifre"}
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isDealerAdmin ? "Bayi admin şifrenizi girin" : "Şifrenizi girin"}
                  className="w-full pl-10 pr-10 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-lg px-4 py-2.5 text-sm flex items-center gap-2">
                <i className="ri-error-warning-line text-base shrink-0"></i>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 font-medium py-2.5 rounded-lg text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Giriş yapılıyor...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line"></i>
                  Giriş Yap
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-background-200/70">
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-4">
                <a
                  href="/forgot-password"
                  className="text-xs text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  Şifremi Unuttum
                </a>
                <span className="text-foreground-300 text-xs">|</span>
                <button
                  type="button"
                  onClick={() => { setShowSupportModal(true); setSupportResult(null); }}
                  className="text-xs text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-customer-service-2-line"></i>
                  Destek Talebi
                </button>
              </div>
              <p className="text-xs text-foreground-400 text-center">
                &copy; 2026 ART POS Barkodlu Satış Sistemleri. Tüm hakları saklıdır.
              </p>
              <p className="text-[10px] text-foreground-300 text-center">
                Developer Ahmet TARIM
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Destek Talebi Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={() => { if (!supportSubmitting) setShowSupportModal(false); }}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950">
                <a id="support-ticket-form">Destek Talebi</a>
              </h4>
              <button
                type="button"
                onClick={() => { if (!supportSubmitting) setShowSupportModal(false); }}
                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <form data-readdy-form onSubmit={handleSupportSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Bayi Kodu</label>
                <input
                  type="text"
                  name="dealer_code"
                  value={supportForm.dealerCode}
                  onChange={(e) => setSupportForm({ ...supportForm, dealerCode: e.target.value })}
                  placeholder="Bayi kodunuz (varsa)"
                  className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Telefon</label>
                <input
                  type="tel"
                  name="phone"
                  value={supportForm.phone}
                  onChange={(e) => setSupportForm({ ...supportForm, phone: e.target.value })}
                  placeholder="Size dönüş yapabileceğimiz telefon numarası"
                  className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Konu <span className="text-accent-500">*</span></label>
                <input
                  type="text"
                  name="subject"
                  value={supportForm.subject}
                  onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
                  placeholder="Sorununuzun konusu"
                  className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Mesaj <span className="text-accent-500">*</span></label>
                <textarea
                  name="message"
                  value={supportForm.message}
                  onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                  rows={4}
                  maxLength={500}
                  placeholder="Sorununuzu detaylı bir şekilde açıklayın..."
                  className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  required
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">{supportForm.message.length}/500</p>
              </div>

              {/* Anti-spam honeypot */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {supportResult && (
                <div className={`rounded-lg px-4 py-2.5 text-sm flex items-center gap-2 ${supportResult.type === "success" ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"}`}>
                  <i className={supportResult.type === "success" ? "ri-check-line" : "ri-error-warning-line"}></i>
                  {supportResult.msg}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowSupportModal(false)}
                  disabled={supportSubmitting}
                  className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={!supportForm.subject.trim() || !supportForm.message.trim() || supportSubmitting}
                  className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {supportSubmitting ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Gönderiliyor
                    </span>
                  ) : (
                    "Gönder"
                  )}
                </button>
              </div>

              {supportResult?.type === "success" && (
                <button
                  type="button"
                  onClick={() => { setShowSupportModal(false); setSupportResult(null); }}
                  className="w-full py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Kapat
                </button>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}